import React, { useState } from "react";
import { Colors } from "../../Constants/colors";
import { useDispatch, useSelector } from "react-redux";
import {
  setShowSideMenuBar,
  setSideMenuOption,
} from "../../Redux/Slices/sideMenu.slice";
import { RootState } from "../../Redux/store";
import { useNavigate } from "react-router-dom";
import { isUserRoleValidForNavigation } from "../../roleConfig";
import {
  FONT_SIZE,
  FONT_WEIGHT,
  ALIGN_ITEMS,
  JUSTIFY_CONTENT,
  FLEX_DIRECTION,
} from "../../Constants/Enums/styles.enum";

interface Props {
  title: string;
  onClick?: () => unknown;
  icon: {
    src: string;
    alt: string;
  };
  navigateTo?: string;
  subButtons?: { name: string; onClick?: () => unknown; navigateTo?: string }[];
}

const SideMenuBtn: React.FC<Props> = ({
  title,
  onClick,
  icon,
  navigateTo,
  subButtons,
}) => {
  const [subButtonClickedIndex, setSubButtonClickedIndex] = useState<
    number | null
  >(null);
  const [isHovered, setIsHovered] = useState(false);

  const dispatch = useDispatch();
  const navigation = useNavigate();

  const buttonClicked = useSelector((state: RootState) => ({
    title: state.sideMenu.title,
    isClicked: state.sideMenu.isClicked,
  }));

  const { role } = useSelector((state: RootState) => state.user);

  let isButtonVisible = false;

  if (navigateTo) {
    isButtonVisible = isUserRoleValidForNavigation({
      path: navigateTo,
      userRole: role,
    });
  } else if (subButtons?.length) {
    subButtons.forEach((button) => {
      if (
        isUserRoleValidForNavigation({
          path: button?.navigateTo,
          userRole: role,
        })
      ) {
        isButtonVisible = true;
        return;
      }
    });
  }

  if (!isButtonVisible) {
    return null;
  }

  const isActive = buttonClicked?.title === title && buttonClicked.isClicked;

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "56px",
          display: "flex",
          alignItems: ALIGN_ITEMS.CENTER,
          justifyContent: JUSTIFY_CONTENT.CENTER,
          backgroundColor: isActive
            ? "rgba(158, 7, 7, 0.6)"
            : isHovered
              ? "rgba(158, 7, 7, 0.4)"
              : "transparent",
          borderLeft: isActive
            ? `3px solid ${Colors.TangerineOrange}`
            : "3px solid transparent",
          color: Colors.White,
          cursor: "pointer",
          transition: "all 0.3s ease",
          position: "relative",
        }}
        onClick={() => {
          onClick?.();
          navigateTo && navigation(navigateTo);
          dispatch(setSideMenuOption(title));
          !subButtons && dispatch(setShowSideMenuBar(false));
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        aria-label={title}
        tabIndex={0}
      >
        <div
          style={{
            display: "flex",
            flexDirection: FLEX_DIRECTION.ROW,
            alignItems: ALIGN_ITEMS.CENTER,
            width: "85%",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: ALIGN_ITEMS.CENTER,
              justifyContent: JUSTIFY_CONTENT.CENTER,
              flexShrink: 0,
            }}
          >
            <img
              src={icon.src}
              alt={icon.alt}
              style={{
                width: "20px",
                height: "20px",
                filter:
                  "brightness(0) saturate(100%) invert(63%) sepia(85%) saturate(1677%) hue-rotate(359deg) brightness(102%) contrast(99%)",
                opacity: isActive ? 1 : 0.9,
                transition: "opacity 0.3s ease",
              }}
            />
          </div>
          <div
            style={{
              flex: 1,
              fontSize: FONT_SIZE.F16,
              fontWeight: isActive ? FONT_WEIGHT.SEMI_BOLD : FONT_WEIGHT.MEDIUM,
              letterSpacing: "0.3px",
              transition: "font-weight 0.3s ease",
            }}
          >
            {title}
          </div>
          {subButtons && subButtons.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: ALIGN_ITEMS.CENTER,
                justifyContent: JUSTIFY_CONTENT.CENTER,
                transition: "transform 0.3s ease",
                transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 8 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.589996 10.59L5.17 6L0.589996 1.41L2 0L8 6L2 12L0.589996 10.59Z"
                  fill={isActive ? Colors.White : Colors.White}
                />
              </svg>
            </div>
          )}
        </div>
      </div>
      {subButtons && (
        <div
          style={{
            maxHeight: isActive ? `${subButtons.length * 52}px` : "0",
            overflow: "hidden",
            transition: "max-height 0.4s ease-in-out",
            backgroundColor: Colors.BurgundyRed,
          }}
        >
          {subButtons.map((button, index) => {
            const isSubButtonVisible = isUserRoleValidForNavigation({
              path: button?.navigateTo,
              userRole: role,
            });

            if (!isSubButtonVisible) {
              return null;
            }

            const isSubActive = subButtonClickedIndex === index;

            return (
              <SubMenuItem
                key={`${title}-${index}`}
                button={button}
                index={index}
                isActive={isSubActive}
                onClick={() => {
                  setSubButtonClickedIndex(index);
                  button.navigateTo && navigation(button.navigateTo);
                  button.onClick?.();
                  dispatch(setShowSideMenuBar(false));
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

interface SubMenuItemProps {
  button: { name: string; onClick?: () => unknown; navigateTo?: string };
  index: number;
  isActive: boolean;
  onClick: () => void;
}

const SubMenuItem: React.FC<SubMenuItemProps> = ({
  button,
  isActive,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        width: "100%",
        height: "52px",
        display: "flex",
        alignItems: ALIGN_ITEMS.CENTER,
        justifyContent: JUSTIFY_CONTENT.CENTER,
        backgroundColor: isActive
          ? "rgba(158, 7, 7, 0.5)"
          : isHovered
            ? "rgba(158, 7, 7, 0.3)"
            : "transparent",
        color: Colors.White,
        cursor: "pointer",
        transition: "all 0.2s ease",
        borderLeft: isActive
          ? `2px solid ${Colors.TangerineOrange}`
          : "2px solid transparent",
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      aria-label={button.name}
      tabIndex={0}
    >
      <div
        style={{
          display: "flex",
          flexDirection: FLEX_DIRECTION.ROW,
          alignItems: ALIGN_ITEMS.CENTER,
          width: "85%",
          gap: "12px",
          paddingLeft: "20px",
        }}
      >
        <div
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: isActive ? Colors.White : Colors.Grey,
            flexShrink: 0,
            transition: "background-color 0.2s ease",
          }}
        />
        <div
          style={{
            flex: 1,
            fontSize: FONT_SIZE.F14,
            fontWeight: isActive ? FONT_WEIGHT.MEDIUM : FONT_WEIGHT.REGULAR,
            opacity: isActive ? 1 : 0.9,
            letterSpacing: "0.2px",
            transition: "all 0.2s ease",
          }}
        >
          {button.name}
        </div>
      </div>
    </div>
  );
};

export default SideMenuBtn;
